import React from 'react';
import { Box, TextField, IconButton, Radio, Checkbox, FormControlLabel, Button, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Question } from '../../../../types/course';

interface QCMEditorProps {
    question: Question;
    onChange: (updatedQuestion: Question) => void;
}

const QCMEditor: React.FC<QCMEditorProps> = ({ question, onChange }) => {
    const options = question.options || [];
    const multiSelect = question.multiSelect || false;
    const correctOptions = question.correctOptions || (question.correctOptionIndex !== undefined ? [question.correctOptionIndex] : []);

    const handleOptionChange = (index: number, value: string) => {
        const newOptions = [...options];
        newOptions[index] = value;
        onChange({ ...question, options: newOptions });
    };

    const handleAddOption = () => {
        onChange({ ...question, options: [...options, ''] });
    };

    const handleRemoveOption = (index: number) => {
        const newOptions = [...options];
        newOptions.splice(index, 1);

        // Update correct options indices
        let newCorrectOptions = correctOptions.filter(i => i !== index).map(i => i > index ? i - 1 : i);

        onChange({
            ...question,
            options: newOptions,
            correctOptions: newCorrectOptions,
            correctOptionIndex: newCorrectOptions.length > 0 ? newCorrectOptions[0] : undefined
        });
    };

    const toggleCorrectOption = (index: number) => {
        let newCorrectOptions: number[];
        if (multiSelect) {
            if (correctOptions.includes(index)) {
                newCorrectOptions = correctOptions.filter(i => i !== index);
            } else {
                newCorrectOptions = [...correctOptions, index];
            }
        } else {
            newCorrectOptions = [index];
        }

        onChange({
            ...question,
            correctOptions: newCorrectOptions,
            correctOptionIndex: newCorrectOptions.length > 0 ? newCorrectOptions[0] : undefined
        });
    };

    return (
        <Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={multiSelect}
                        onChange={(e) => onChange({ ...question, multiSelect: e.target.checked, correctOptions: [] })}
                    />
                }
                label="Multiple Answers Allowed"
                sx={{ mb: 2 }}
            />

            {options.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <FormControlLabel
                        control={
                            multiSelect ? (
                                <Checkbox
                                    checked={correctOptions.includes(index)}
                                    onChange={() => toggleCorrectOption(index)}
                                    color="success"
                                />
                            ) : (
                                <Radio
                                    checked={correctOptions.includes(index)}
                                    onChange={() => toggleCorrectOption(index)}
                                    color="success"
                                />
                            )
                        }
                        label=""
                        sx={{ mr: 1 }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: correctOptions.includes(index) ? 'rgba(46, 125, 50, 0.04)' : 'transparent'
                            }
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={() => handleRemoveOption(index)}
                        disabled={options.length <= 2}
                        sx={{ ml: 1, opacity: options.length <= 2 ? 0.3 : 1 }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}

            <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddOption}
                size="small"
                sx={{ mt: 1 }}
            >
                Add Option
            </Button>
        </Box>
    );
};

export default QCMEditor;
